import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable } from "../../components/DataTable/TableHook";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { stoppagesMutations, wasQueued } from "../../offline/stoppagesMutations";
import TimeCounter from "../../components/TimeCounter/TimeCounter";
import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { StoppageSubTypeDto } from "../../DTOs/Stoppage/StoppageSubTypeDto";
import { StoppageSubTypeOption } from "../../components/Hooks/useStoppageTypes";
import { StoppageTypeDto } from "../../DTOs/Stoppage/StoppageTypeDto";
import { TimePicker } from "../../components/TimePicker/TimePicker";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { shiftDurationHours, shiftStartHour } from "../../offline/shiftBands";
import { adjustColorBrightness } from "../../utilities/helpers/adjustColorBrightness";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import DeleteButton from "../../components/Buttons/DeleteButton";
import SaveButton from "../../components/Buttons/SaveButton";
import { TripleShift } from "../../types/Shift";
import adjustTimeForMidnightCross from "../../utilities/helpers/adjustTimeForMidnightCross";
import { usePendingEntityIds } from "../../offline/usePendingEntityIds";
import { appendCachedPlantStoppage, appendCachedStoppage, patchCachedOperationAvailability, patchCachedOperationStatus } from "../../offline/patchCachedStoppages";
import { useCanWrite, useIsReadOnly } from "../../utilities/helpers/roleUtilities";
import { vocabularyName } from "../../i18n/vocabulary";

type InsertRowState = {
  downtimeTypeId: number;
  downtimeSubTypeId: number;
  timeIn: DateTime;
  timeOut?: DateTime | null;
}

interface InsertRowProps {
  state: InsertRowState;
  setState: React.Dispatch<React.SetStateAction<InsertRowState>>;
  onAdd: () => Promise<boolean>;
  stoppageTypeGroupOptions: StoppageSubTypeOption[];
  shiftStartTime: DateTime;
  shiftEndTime: DateTime;
  selectedDate: DateTime;
  isReadOnly: boolean
}

const InsertInputsRow = ({
  state,
  setState,
  onAdd,
  stoppageTypeGroupOptions,
  shiftStartTime,
  shiftEndTime,
  selectedDate,
  isReadOnly
}: InsertRowProps) => {
  const { t } = useTranslation("production");
  const opensDowntime = state.timeOut == null
    && stoppageTypeGroupOptions.some(o => o.downtimeTypeId === state.downtimeTypeId && o.isDowntime);
  const updateField = useCallback(<K extends keyof InsertRowState>(
    key: K,
    value: InsertRowState[K]
  ) => {
    setState(prev => ({
      ...prev,
      [key]: value
    }));
  }, [setState]);
  return (
    <ul className="flex gap-2 border-b border-theme-border text-theme-text">
      <li key="typeAndSubtype" className="basis-[40%] flex items-center justify-center text-[0.9rem] w-full">
        <Autocomplete
          disabled={isReadOnly}
          size="small"
          sx={{ width: '100%' }}
          options={[...stoppageTypeGroupOptions].sort((a, b) => a.stoppageType.localeCompare(b.stoppageType))}
          getOptionLabel={(option) => vocabularyName("stoppageSubTypes", option.label)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={stoppageTypeGroupOptions.find(o => o.value === state.downtimeSubTypeId) || null}
          groupBy={(option) => option.stoppageType}
          renderGroup={(params) => {
            const stoppageTypeHeadingColor = stoppageTypeGroupOptions.find(option => option.stoppageType === params.group)?.groupColor!;
            const contrastColor = getContrastTextColor(stoppageTypeHeadingColor);

            return (
              <div key={params.key}>
                <div
                  className="px-3 py-1 text-sm font-semibold"
                  style={{
                    backgroundColor: stoppageTypeHeadingColor,
                    color: contrastColor
                  }}
                >
                  {vocabularyName("stoppageTypes", params.group)}
                </div>
                <ul
                  style={{
                    color: contrastColor === "#000000" ? adjustColorBrightness(stoppageTypeHeadingColor, -40) : stoppageTypeHeadingColor
                  }}
                >
                  {params.children}
                </ul>
              </div>
            )
          }}
          onChange={(e, newValue) => {
            setState(prev => ({
              ...prev,
              downtimeSubTypeId: newValue ? newValue.value : 0,
              downtimeTypeId: newValue?.downtimeTypeId ?? 0
            }))
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("nestedOperations.selectStoppageType")}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </li>

      <li key="timeIn" className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <TimePicker
          initialDate={state.timeIn}
          onChange={e => {
            if (!e || !DateTime.isDateTime(e)) {
              updateField("timeIn", e!);
              return;
            }

            const adjustedDateTime = adjustTimeForMidnightCross(
              e,
              selectedDate,
              shiftStartTime,
              shiftEndTime
            );

            updateField("timeIn", adjustedDateTime);
          }}
          minValue={shiftStartTime}
          maxValue={shiftEndTime}
          disabled={isReadOnly}
        />
      </li>

      <li key="timeOut" className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <div className="flex flex-col items-center w-full gap-1">
          {state.timeOut && (
            <span className="text-xs font-medium">
              {state.timeOut.toFormat('yyyy-MM-dd')}
            </span>
          )}
          <TimePicker
            initialDate={state.timeOut}
            onChange={e => {
              if (!e || !DateTime.isDateTime(e)) {
                updateField("timeOut", e!);
                return;
              }
              const adjustedDateTime = adjustTimeForMidnightCross(
                e,
                selectedDate,
                shiftStartTime,
                shiftEndTime
              );
              updateField("timeOut", adjustedDateTime);
            }}
            minValue={shiftStartTime}
            maxValue={shiftEndTime}
            disabled={isReadOnly}
          />
        </div>
      </li>

      <li key="timeDown" className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        {state.timeOut != null && state.timeIn !== null && state.timeOut > state.timeIn ?
          <TimeCounter
            startedAt={state.timeIn}
            endedAt={state.timeOut}
          />
          :
          <span>-</span>
        }
      </li>

      <li key="actions" className="basis-[15%] flex items-center justify-center text-[0.9rem] w-full">
        <SaveButton
          handleSave={onAdd}
          requireConfirmation={opensDowntime}
          confirmMessage={opensDowntime ? t("nestedOperations.ongoingConfirm") : undefined}
          buttonLabel={t("actions.add")}
          disabled={isReadOnly}
          styleClasses="!bg-theme-info hover:!bg-theme-info-hover"
        />
      </li>
    </ul>
  )
}

interface NestedTableProps {
  plantId: number;
  /** The machine's name, as the parent row shows it. */
  machineName?: string;
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: StoppageSubTypeOption[];
  parentRowStatus: "UP" | string;
  refreshParent: (plantId: number) => Promise<boolean>;
  registerRefreshFn: (fn: () => void) => void;
}

const NestedOperationsTable: React.FC<NestedTableProps> = ({
  plantId,
  machineName,
  selectedDate,
  selectedShift,
  stoppageTypeGroupOptions,
  parentRowStatus,
  refreshParent,
  registerRefreshFn
}) => {
  const { t } = useTranslation("production");
  const isReadOnly = useIsReadOnly("Stoppages");
  const canDelete = useCanWrite("Stoppages.DeleteStoppage");
  const pendingStoppageIds = usePendingEntityIds('stoppage')
  const { shiftStartTime: rawStart, shiftEndTime: rawEnd } = useShiftTimes();
  // Note: the afternoon band's hours come from the configured ShiftBands rather than the literal 14 and
  // 22 that used to sit here, so re-timing or re-lengthening the afternoon shift in appsettings moves
  // these pickers with it instead of leaving them clamped to the old window.
  const afternoonStartHour = shiftStartHour("A");
  const afternoonEndHour = (afternoonStartHour + shiftDurationHours("A")) % 24;
  const shiftStartTime = useMemo(() => selectedShift[0] === "A"
    ? selectedDate.set({ hour: afternoonStartHour, minute: 0 })
    : rawStart, [selectedShift, rawStart, selectedDate, afternoonStartHour]);

  const shiftEndTime = useMemo(() => selectedShift[0] === "A"
    ? selectedDate.set({ hour: afternoonEndHour, minute: 0 })
    : rawEnd, [selectedShift, rawEnd, selectedDate, afternoonEndHour]);

  const [insertState, setInsertState] = useState<InsertRowState>({
    downtimeTypeId: 0,
    downtimeSubTypeId: 0,
    timeIn: shiftStartTime,
    timeOut: null
  });

  const { enqueueSnackbar } = useSnackbar();
  const getRows = useCallback(() => stoppagesAjax.getStoppagesByShiftAndPlant(selectedDate, selectedShift, plantId), [selectedDate, selectedShift, plantId]);

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    refresh,
    removeRowSource,
    updateRowSource,
    appendRowSource
  } = useDataTable<StoppageDto>({
    queryKey: ['operations', 'plantStoppages', selectedDate.toISODate(), selectedShift, plantId, parentRowStatus],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: 5,
    enabled: true
  });

  const handleAddRow = useCallback(async () => {
    let isValid = true;

    if (insertState.downtimeSubTypeId === 0) {
      enqueueSnackbar(t("nestedOperations.subtypeRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.timeIn === null) {
      enqueueSnackbar(t("validation.startTimeRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (insertState.timeOut && insertState.timeIn && insertState.timeOut < insertState.timeIn) {
      enqueueSnackbar(t("nestedOperations.timeOutBeforeTimeIn"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await stoppagesMutations.addStoppage(new StoppageDto({
      plantId: plantId,
      plant: { id: plantId, plantRef: machineName } as StoppageDto['plant'],
      startTime: insertState.timeIn,
      endTime: insertState.timeOut ?? undefined,
      stoppageTypeId: insertState.downtimeTypeId,
      stoppageSubTypeId: insertState.downtimeSubTypeId,
      incident: false
    }));

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    setInsertState({
      downtimeTypeId: 0,
      downtimeSubTypeId: 0,
      timeIn: shiftStartTime,
      timeOut: null
    })

    enqueueSnackbar(t("nestedOperations.stoppageAdded"), { variant: 'success', autoHideDuration: 3000 })

    const subType = stoppageTypeGroupOptions.find(o => o.value === insertState.downtimeSubTypeId);
    const queued = wasQueued(response);
    const dataToAppend = !queued ? response.data! : new StoppageDto({
      ...response.data!,
      stoppageSubType: new StoppageSubTypeDto({
        stoppageSubTypeId: insertState.downtimeSubTypeId,
        stoppageSubType: subType?.label ?? '',
        stoppageTypeId: insertState.downtimeTypeId,
      }),
      stoppageType: new StoppageTypeDto({
        stoppageTypeId: insertState.downtimeTypeId,
        stoppageType: subType?.stoppageType ?? '',
        colourHex: subType?.groupColor,
      })
    });
    appendRowSource(dataToAppend);

    if (queued) {
      appendCachedStoppage(dataToAppend, selectedDate.toISODate()!, selectedShift);
      appendCachedPlantStoppage(dataToAppend, selectedDate.toISODate()!, selectedShift, plantId);
      if (!insertState.timeOut) {
        patchCachedOperationStatus(plantId, selectedDate.toISODate()!, selectedShift, {
          status: subType?.stoppageType ?? 'Maintenance Down',
          statusDescription: `${subType?.stoppageType ?? ''} - ${subType?.label ?? ''}`.trim(),
          stoppageColourHub: subType?.groupColor,
          stoppageId: response.data?.stoppageId,
          currentStoppageTypeId: insertState.downtimeTypeId,
          currentStoppageSubTypeId: insertState.downtimeSubTypeId,
          timeIn: insertState.timeIn,
        });
      }
    }

    return await refreshParent(plantId);
  }, [insertState.downtimeSubTypeId, insertState.timeIn, insertState.timeOut, insertState.downtimeTypeId, plantId, machineName, stoppageTypeGroupOptions, selectedDate, selectedShift, enqueueSnackbar, refreshParent, shiftStartTime, appendRowSource, t]);

  const deleteRow = useCallback(async (id: number) => {
    const response = await stoppagesMutations.deleteStoppage(id);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    removeRowSource((row) => row.stoppageId === id);

    enqueueSnackbar(t("nestedOperations.stoppageDeleted"), { variant: 'success', autoHideDuration: 3000 })

    return refreshParent(plantId);
  }, [plantId, refreshParent, enqueueSnackbar, removeRowSource, t]);

  const handleSaveRow = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.stoppageSubTypeId) {
      enqueueSnackbar(t("nestedOperations.subtypeRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (stoppage.startTime === null) {
      enqueueSnackbar(t("validation.startTimeRequired"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await stoppagesMutations.updateStoppage(stoppage);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    updateRowSource((row) => row.stoppageId === response.data?.stoppageId, response.data!);

    if (wasQueued(response) && stoppage.endTime) {
      patchCachedOperationAvailability(plantId, selectedDate.toISODate()!, selectedShift, stoppage.stoppageId);
    }

    return await refreshParent(plantId);
  }, [plantId, selectedDate, selectedShift, refreshParent, enqueueSnackbar, updateRowSource, t]);

  const translateFn = useCallback((r: StoppageDto): TableRow => {
    return {
      id: r.stoppageId ?? 0,
      values: [
        {
          name: "Downtime Type",
          label: t("nestedOperations.downtimeType"),
          type: "custom",
          value: r.stoppageSubTypeId,
          tailwindClasses: "basis-[40%] py-3",
          render: (_value, _row, helpers) => {
            const currentSubTypeId: number = helpers.draft.get();
            const selectedOption = stoppageTypeGroupOptions.find(o => o.value === currentSubTypeId) ?? null;

            return (
              <div className="flex items-center gap-2 w-full">
                <Autocomplete
                  disabled={isReadOnly}
                  size="small"
                  sx={{ width: '100%' }}
                  options={[...stoppageTypeGroupOptions].sort((a, b) => a.stoppageType.localeCompare(b.stoppageType))}
                  getOptionLabel={(option) => vocabularyName("stoppageSubTypes", option.label)}
                  isOptionEqualToValue={(option, val) => option.value === val.value}
                  value={selectedOption}
                  groupBy={(option) => option.stoppageType}
                  renderGroup={(params) => {
                    const stoppageTypeHeadingColor = stoppageTypeGroupOptions.find(option => option.stoppageType === params.group)?.groupColor!;
                    const contrastColor = getContrastTextColor(stoppageTypeHeadingColor);

                    return (
                      <div key={params.key}>
                        <div
                          className="px-3 py-1 text-sm font-semibold"
                          style={{
                            backgroundColor: stoppageTypeHeadingColor,
                            color: contrastColor
                          }}
                        >
                          {vocabularyName("stoppageTypes", params.group)}
                        </div>
                        <ul
                          style={{
                            color: contrastColor === "#000000" ? adjustColorBrightness(stoppageTypeHeadingColor, -40) : stoppageTypeHeadingColor
                          }}
                        >
                          {params.children}
                        </ul>
                      </div>
                    )
                  }}
                  onChange={(_, newValue) => {
                    helpers.draft.set(newValue ? newValue.value : 0);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t("nestedOperations.selectStoppageType")} />
                  )}
                />
                {(pendingStoppageIds.has(r.stoppageId ?? 0) || (r.stoppageId ?? 0) < 0) && (
                  <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-theme-warning text-white font-medium shrink-0 leading-none">
                    {t("nestedOperations.unsynced")}
                  </span>
                )}
              </div>
            );
          }
        },
        {
          name: "Time In",
          label: t("columns.timeIn"),
          type: "date",
          value: r.startTime,
          tailwindClasses: "basis-[15%] py-3",
          render: (value) => value.value.toFormat('yyyy-MM-dd HH:mm')
        },
        {
          name: "Time Out",
          label: t("columns.timeOut"),
          type: "date",
          value: r.endTime,
          render: (value, _row, helpers) => {
            return parentRowStatus !== "UP" && r.endTime == null
              ? (
                <div className="flex flex-col items-center w-full gap-1">
                  {value.value && (
                    <span className="text-xs font-medium text-theme-text">
                      {value.value.toFormat('yyyy-MM-dd')}
                    </span>
                  )}
                  <TimePicker
                    initialDate={value.value}
                    onChange={e => {
                      if (!e || !DateTime.isDateTime(e)) {
                        helpers.draft.set(e);
                        helpers.draft.set(e, "Time Down");
                        helpers.draft.set(e, "Actions");
                        return;
                      }
                      const adjustedDateTime = adjustTimeForMidnightCross(
                        e,
                        selectedDate,
                        shiftStartTime,
                        shiftEndTime
                      );
                      helpers.draft.set(adjustedDateTime);
                      helpers.draft.set(adjustedDateTime, "Time Down");
                      helpers.draft.set(adjustedDateTime, "Actions");
                    }}
                    minValue={shiftStartTime > r.startTime ? shiftStartTime : r.startTime}
                    maxValue={shiftEndTime}
                    disabled={isReadOnly}
                  />
                </div>
              )
              : (<span>{value.value?.toFormat('yyyy-MM-dd HH:mm')}</span>)
          },
          tailwindClasses: "basis-[15%] py-3"
        },
        {
          name: "Time Down",
          label: t("nestedOperations.timeDown"),
          type: "custom",
          value: null,
          render: (_, row) => {
            const startTimeValue: DateTime = row.values.find(v => v.name === "Time In")?.value;
            const endTime: DateTime | undefined = row.values.find(v => v.name === "Time Out")?.value ?? null;
            const naturalEnd = endTime ?? null;

            return (<TimeCounter startedAt={startTimeValue} endedAt={naturalEnd} />);
          },
          tailwindClasses: "basis-[15%] py-3"
        },
        {
          name: 'Actions',
          label: t("fields.actions"),
          type: 'custom',
          value: r.endTime,
          render: (_, row) => {
            const currentSubTypeId: number = row.values.find(v => v.name === "Downtime Type")?.value ?? r.stoppageSubTypeId;
            const selectedType = stoppageTypeGroupOptions.find(o => o.value === currentSubTypeId);

            return (
              <div className="flex justify-center align-items-center flex-wrap gap-1">
                {!isReadOnly &&
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={async () =>
                      await handleSaveRow(new StoppageDto({
                        ...r,
                        stoppageId: row.id,
                        plantId: plantId,
                        startTime: row.values.find(v => v.name === "Time In")?.value,
                        endTime: row.values.find(v => v.name === "Time Out")?.value,
                        stoppageSubTypeId: currentSubTypeId,
                        stoppageTypeId: selectedType?.downtimeTypeId ?? r.stoppageTypeId
                      }))
                    }
                    className="border-none bg-theme-success hover:bg-theme-success-hover text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed font-bold py-2 px-4 rounded-sm whitespace-nowrap transition">
                    {t("actions.save")}
                  </button>
                }
                {canDelete && <DeleteButton
                  handleDelete={() => deleteRow(row.id)}
                  disabled={isReadOnly}
                  requireConfirmation
                />}
              </div>
            )

          },
          tailwindClasses: "basis-[15%] py-3"
        }
      ]
    };
  }, [parentRowStatus, shiftStartTime, shiftEndTime, isReadOnly, canDelete, selectedDate, plantId, pendingStoppageIds, stoppageTypeGroupOptions, deleteRow, handleSaveRow, t]);

  const displayData = useMemo(() => rows.map(translateFn), [rows, translateFn]);

  useEffect(() => {
    registerRefreshFn(refresh);
  }, [refresh, registerRefreshFn]);

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      insertRowRenderer={parentRowStatus === "UP" ? () => (
        <InsertInputsRow
          state={insertState}
          setState={setInsertState}
          onAdd={handleAddRow}
          shiftStartTime={shiftStartTime}
          shiftEndTime={shiftEndTime}
          stoppageTypeGroupOptions={stoppageTypeGroupOptions}
          selectedDate={selectedDate}
          isReadOnly={isReadOnly}
        />
      )
        : undefined
      }
      isNestedTable={true}
      overrideHeaderCells={parentRowStatus === "UP" ? [
        { name: "Downtime Type", label: t("nestedOperations.downtimeType"), tailwindClasses: "basis-[40%]" },
        { name: "Time In", label: t("columns.timeIn"), tailwindClasses: "basis-[15%]" },
        { name: "Time Out", label: t("columns.timeOut"), tailwindClasses: "basis-[15%]" },
        { name: "Time Down", label: t("nestedOperations.timeDown"), tailwindClasses: "basis-[15%]" },
        { name: "Action", label: t("nestedOperations.action"), tailwindClasses: "basis-[15%]" },
      ] : undefined}
    />
  );
};

export default NestedOperationsTable;
